import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert, Box, Button, Chip, CircularProgress, FormControl, FormControlLabel, InputLabel, MenuItem, Paper,
    Select, Stack, Switch, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField,
    Typography,
} from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { MerchReportService } from '../../services/merchReport.service';
import { AuthService } from '../../services/auth.service';
import { useToast } from '../../context/ToastContext';
import { IOrderDashboardFilters, IOrderDashboardRow } from '../../interfaces/garments/merchReport.interface';
import { BuyerOrderStatus } from '../../interfaces/garments/buyerOrder.interface';
import { TNA_MILESTONE_TYPE_LABELS } from '../../interfaces/garments/tnaMilestone.interface';
import { triggerDownload } from '../statutoryRegisters/shared';
import { enumLabel } from '../../utils/garmentsLabels';
import { toLocalIsoDate, formatDate } from '../../utils/dateUtils';

const ORDER_STATUSES: BuyerOrderStatus[] = ['DRAFT', 'CONFIRMED', 'IN_PRODUCTION', 'SHIPPED', 'CLOSED', 'CANCELLED'];

/**
 * Order dashboard (garments-tna-calendar-order-dashboard, task 4) — a filterable table of buyer
 * orders with T&A progress and a delay flag, backed by `MerchandisingReportController
 * #getOrderDashboard`. Export XLSX mirrors `OrderBookReport`'s pattern (blob + `triggerDownload`).
 */
export function OrderDashboard() {
    const toast = useToast();

    const [rows, setRows] = useState<IOrderDashboardRow[]>([]);
    const [truncated, setTruncated] = useState(false);
    const [loading, setLoading] = useState(true);
    const [downloading, setDownloading] = useState(false);

    const [status, setStatus] = useState<BuyerOrderStatus | ''>('');
    const [shipFrom, setShipFrom] = useState('');
    const [shipTo, setShipTo] = useState('');
    const [delayedOnly, setDelayedOnly] = useState(false);
    const [includeAll, setIncludeAll] = useState(false);

    const buildFilters = useCallback((): IOrderDashboardFilters => ({
        status: status || undefined,
        shipFrom: shipFrom || undefined,
        shipTo: shipTo || undefined,
        delayedOnly,
        includeAll,
    }), [status, shipFrom, shipTo, delayedOnly, includeAll]);

    const load = useCallback(async () => {
        const institutionId = AuthService.getSelectedInstitution();
        if (!institutionId) return;
        setLoading(true);
        try {
            const data = await MerchReportService.getOrderDashboard(institutionId, buildFilters());
            setRows(data.rows);
            setTruncated(data.truncated);
        } catch (e) {
            toast.showApiError(e, 'Failed to load order dashboard');
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [buildFilters]);

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [delayedOnly, includeAll]);

    const clearFilters = () => {
        setStatus('');
        setShipFrom('');
        setShipTo('');
        setDelayedOnly(false);
        setIncludeAll(false);
    };

    const handleExport = async () => {
        const institutionId = AuthService.getSelectedInstitution();
        if (!institutionId) return;
        setDownloading(true);
        try {
            const blob = await MerchReportService.downloadOrderDashboardXlsx(institutionId, buildFilters());
            triggerDownload(blob, `order-dashboard-${toLocalIsoDate()}.xlsx`);
        } catch (e) {
            toast.showApiError(e, 'Failed to export order dashboard');
        } finally {
            setDownloading(false);
        }
    };

    const nextMilestoneDisplay = (row: IOrderDashboardRow): string => {
        if (!row.nextMilestoneType) return '—';
        const label = TNA_MILESTONE_TYPE_LABELS[row.nextMilestoneType] ?? row.nextMilestoneType;
        return row.nextMilestoneTargetDate ? `${label} (${formatDate(row.nextMilestoneTargetDate)})` : label;
    };

    return (
        <Box sx={{ p: 3 }} data-help-page="merchandising.orderDash.page">
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h5">Order Dashboard</Typography>
                <Button
                    data-help="merchandising.orderDash.export"
                    variant="contained" size="small" startIcon={<FileDownloadIcon />}
                    onClick={handleExport} disabled={loading || downloading}
                >
                    Download
                </Button>
            </Stack>

            <Paper variant="outlined" sx={{ p: 2, mb: 2 }} data-help-section="merchandising.orderDash.filter">
                <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap alignItems="center">
                    <FormControl data-help="merchandising.orderDash.status" size="small" sx={{ flex: '1 1 160px', maxWidth: 280 }}>
                        <InputLabel>Status</InputLabel>
                        <Select
                            label="Status" value={status}
                            onChange={e => setStatus(e.target.value as BuyerOrderStatus | '')}
                        >
                            <MenuItem value="">All</MenuItem>
                            {ORDER_STATUSES.map(s => <MenuItem key={s} value={s}>{enumLabel(s)}</MenuItem>)}
                        </Select>
                    </FormControl>
                    <TextField
                        data-help="merchandising.orderDash.shipFrom" size="small" label="Ship From" type="date" InputLabelProps={{ shrink: true }} sx={{ flex: '1 1 150px', maxWidth: 200 }}
                        value={shipFrom} onChange={e => setShipFrom(e.target.value)}
                    />
                    <TextField
                        data-help="merchandising.orderDash.shipTo" size="small" label="Ship To" type="date" InputLabelProps={{ shrink: true }} sx={{ flex: '1 1 150px', maxWidth: 200 }}
                        value={shipTo} onChange={e => setShipTo(e.target.value)}
                    />
                    <FormControlLabel
                        data-help="merchandising.orderDash.delayedOnly"
                        control={(
                            <Switch
                                checked={delayedOnly}
                                onChange={e => setDelayedOnly(e.target.checked)}
                                inputProps={{ 'aria-label': 'delayed only' }}
                            />
                        )}
                        label="Delayed only"
                    />
                    <FormControlLabel
                        data-help="merchandising.orderDash.includeAll"
                        control={(
                            <Switch
                                checked={includeAll}
                                onChange={e => setIncludeAll(e.target.checked)}
                                inputProps={{ 'aria-label': 'include closed/cancelled' }}
                            />
                        )}
                        label="Include closed/cancelled"
                    />
                    <Button data-help="merchandising.orderDash.apply" variant="outlined" size="small" onClick={load} disabled={loading}>Apply</Button>
                    <Button data-help="merchandising.orderDash.clear" size="small" onClick={clearFilters}>Clear</Button>
                </Stack>
            </Paper>

            {truncated && (
                <Alert severity="warning" sx={{ mb: 2 }} data-help-section="merchandising.orderDash.notice">
                    Showing the first 2000 orders — narrow the filters to see the rest.
                </Alert>
            )}

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
            ) : (
                <TableContainer component={Paper} data-testid="order-dashboard-table" data-help-section="merchandising.orderDash.table">
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell data-help="merchandising.orderDash.col.order">Order #</TableCell>
                                <TableCell data-help="merchandising.orderDash.col.buyer">Buyer</TableCell>
                                <TableCell data-help="merchandising.orderDash.col.shipDate">Ship Date</TableCell>
                                <TableCell data-help="merchandising.orderDash.col.status">Status</TableCell>
                                <TableCell align="right" data-help="merchandising.orderDash.col.progress">Progress</TableCell>
                                <TableCell data-help="merchandising.orderDash.col.delayed">Delayed</TableCell>
                                <TableCell data-help="merchandising.orderDash.col.next">Next Milestone</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={7} align="center">
                                        <Typography color="text.secondary" sx={{ py: 4 }}>
                                            No orders match this filter.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                            {rows.map(row => (
                                <TableRow key={row.orderId} hover data-testid={`dashboard-row-${row.orderId}`}>
                                    <TableCell>{row.orderNumber}</TableCell>
                                    <TableCell>{row.buyerName}</TableCell>
                                    <TableCell>{formatDate(row.shipDate)}</TableCell>
                                    <TableCell><Chip size="small" label={enumLabel(row.status)} /></TableCell>
                                    <TableCell align="right">
                                        {row.milestoneCompleted}/{row.milestoneTotal} ({row.progressPct}%)
                                    </TableCell>
                                    <TableCell>
                                        {row.delayed ? <Chip size="small" color="error" label="Delayed" /> : '—'}
                                    </TableCell>
                                    <TableCell>{nextMilestoneDisplay(row)}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    );
}

export default OrderDashboard;
